import * as Sentry from '@sentry/react';
import { isNoiseEvent } from '@/modules/sentry-noise';
import type { router } from './routes';

const IS_DEV = !!import.meta.env.VITE_IS_LOCALHOST;
const IS_PROD = !!import.meta.env.VITE_BACKEND_IS_PRODUCTION;

const DSN_PROD =
  'https://d59867387d1d310fedb8374a7ec9ee27@o436067.ingest.us.sentry.io/4506728861990912';
const DSN_STAGE =
  'https://287b3ac0bc08cfce0f7acbe80ee41183@o436067.ingest.us.sentry.io/4506733002358784';

if (!IS_DEV) {
  Sentry.init({
    dsn: IS_PROD ? DSN_PROD : DSN_STAGE,
    environment: IS_PROD ? 'production' : 'stage',
    release: __COMMIT_HASH__,
    tunnel: '/tunnel',
    // The privacy policy states that no IP address is transmitted and no
    // session recording is collected: no Replay, and every collection
    // category off, each spelled out — sendDefaultPii is inert once
    // dataCollection is set, and the SDK's own PII-off mapping still sends
    // cookies and headers minus a deny list. userInfo is the browser's one
    // IP source (the {{auto}} marker on sessions). urlQueryParams reaches no
    // browser surface at SDK 10.73 — the page URL, breadcrumbs, spans and
    // failed-request events keep their query strings — and is set for parity
    // with the server, where it strips the incoming request's query.
    // frameContextLines keeps 7 (the option default is 5).
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      stackFrameVariables: false,
      frameContextLines: 7,
    },
    tracesSampleRate: 0.1,
    integrations: [Sentry.httpClientIntegration()],
    beforeSend(event, hint) {
      return isNoiseEvent(event, hint.originalException) ? null : event;
    },
  });
}

// The router module loads after this init on purpose — importing it here
// would put its whole module graph ahead of the error capture — so the
// tracing integration joins once the router exists.
export function attachRouterTracing(appRouter: typeof router): void {
  if (IS_DEV) return;
  Sentry.addIntegration(
    Sentry.tanstackRouterBrowserTracingIntegration(appRouter),
  );
}
